import { ICON_MAPPING, Typo } from "@pepperstone/pepper-ui-kit";
import React, { PropsWithChildren, ReactNode } from "react";

import { IconButton } from "../IconButton";

export type SideMenuProps = PropsWithChildren & {
  isOpen: boolean;
  onClose: () => void;
};

export const SideMenu = ({ children, isOpen, onClose }: SideMenuProps) => {
  const menuState = isOpen ? "opened" : "opacity-0";

  return (
    <div
      className={`shadow-box-hover duration-400 ease-ease fixed bottom-0 end-0 top-0 z-40 w-full translate-x-full bg-white pe-0 transition-transform md:w-[400px] rtl:-translate-x-full [&.opened]:translate-x-0 ${menuState}`}
    >
      <div className="sub-navigation flex h-full w-full flex-col items-start justify-between overflow-y-auto p-4">
        <IconButton
          onClick={onClose}
          size="tiny"
          icon="cross"
          aria-label="Close menu"
        />
        {children}
      </div>
    </div>
  );
};

export const SideMenuContent = ({ children }: PropsWithChildren) => (
  <div className="flex w-full flex-1 flex-col gap-4">{children}</div>
);

export const SideMenuFooter = ({ children }: PropsWithChildren) => (
  <div className="flex w-full flex-col pb-16">{children}</div>
);

export type SideMenuProfileBannerProps = {
  welcomeText: string;
  profileLabel?: string;
  viewProfileLabel?: string;
  linkComponent: ({
    className,
    children,
  }: {
    className: string;
    children: ReactNode;
  }) => ReactNode;
};

export const SideMenuProfileBanner = ({
  welcomeText,
  profileLabel,
  viewProfileLabel,
  linkComponent,
}: SideMenuProfileBannerProps) => {
  const { profile: PersonIcon } = ICON_MAPPING;

  const LinkComponent = linkComponent;

  return (
    <div className="flex w-full flex-row items-center gap-4 py-4">
      <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full bg-semantic-surface-foreground text-icon-base">
        <PersonIcon size={22} />
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="font-sans font-semibold">
          {`${welcomeText}${profileLabel ? ` ${profileLabel}` : ""}`}
        </h3>
        {viewProfileLabel && (
          <Typo elem="p" type="body" size="sm">
            <LinkComponent className="hover:text-blue text-text-secondary transition-colors duration-300 ease-in-out">
              {viewProfileLabel}
            </LinkComponent>
          </Typo>
        )}
      </div>
    </div>
  );
};

export type SideMenuNavItemProps = {
  icon: ReactNode;
  label: string;
  isExternalLink: boolean;
  linkComponent: ({
    children,
    className,
  }: {
    children: React.ReactNode;
    className: string;
  }) => ReactNode;
};

export const SideMenuNavItem = ({
  icon,
  label,
  isExternalLink,
  linkComponent,
}: SideMenuNavItemProps): React.JSX.Element => {
  const SubdueIcon = ICON_MAPPING.subdue;

  const LinkComponent = linkComponent;

  return (
    <LinkComponent className="flex h-10 w-full items-center justify-between text-text-primary transition-colors duration-300 ease-in-out hover:text-text-link-hover">
      <span className="flex items-center gap-4 p-2">
        {icon && (
          <span className="flex h-6 w-6 items-center justify-center">
            {icon}
          </span>
        )}
        <Typo
          elem="p"
          type="label"
          size="md"
          responsiveClasses="sm:ps-text-label-md-mobile"
        >
          {label}
        </Typo>
      </span>
      {isExternalLink && (
        <span className="flex aspect-square items-center justify-center text-icon-subdued">
          <SubdueIcon size={13} />
        </span>
      )}
    </LinkComponent>
  );
};

export const SideMenuSeparator = () => (
  <div className="h-px w-full bg-border-primary-base" />
);
